import { SessionBirthModelChoice } from "./SessionBirthModelChoice";
import { GoalAssignment } from "./GoalAssignment";
import {
  Bot,
  Files,
  Globe2,
  Goal,
  LayoutDashboard,
  MessageSquarePlus,
  Plus,
  SettingsIcon,
  X,
} from "lucide-react";
import {
  ROOM_ID_PATTERN,
  entrySessionOf,
  isRootSession,
  roomOfSession,
  workroomBoardKind,
  type WorldRoomDirectory,
  type ModelSelection,
} from "@cleo/contracts";
import { activateOrCreateWorkroom } from "../neucleos/workroomDoor";
import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useRovingTablist } from "../hooks/useRovingTablist";
import { useScrollFade } from "../hooks/useScrollFade";
import { useSlidingTabIndicator } from "../hooks/useSlidingTabIndicator";
import { cn, randomUUID } from "../lib/utils";
import { recordWorldSessionBirthAttempt } from "../neucleos/worldSessionBirthAttemptsClient";
import {
  openWorldSession,
  setWorldSessionEngagement,
  orderContextBirthSelection,
} from "../neucleos/worldSessionClient";
import { isSingletonBoardKind, useBoardDeckStore } from "../boardDeckStore";
import {
  roomUniverseCoordinateKey,
  useRoomUniverseStore,
  type RoomUniverseEntry,
} from "../neucleos/roomUniverseStore";
import { useRailLawStore } from "../neucleos/railLawStore";
import { MergeTabMark } from "./MergeTabMark";
import { RailLawControl } from "./RailLawControl";
import { useNeucleosWorldStore } from "../neucleos/worldStore";
import {
  useEnsureWorldFilesProject,
  useWorldSessionLaunchProjects,
} from "../neucleos/useWorldSessionLaunchContext";
import {
  resolveWorldFilesRoot,
  resolveWorldSessionLaunchGrounding,
  useWorldWorkspaceBindings,
} from "../neucleos/worldWorkspaceBinding";
import { openWorldRoomConnections } from "../neucleos/worldViewController";
import { deskSessionMenuEntries, useLaunchableWorkroomsOutcome } from "../neucleos/workroomLaunch";
import {
  createWorldSessionExecutionLauncher,
  type WorldSessionExecutionBirthOutcome,
} from "../neucleos/worldSessionExecutionBirth";
import { initializeBoardContextBirth } from "../neucleos/boardContextBirth";
import { useSessionLivenessRollups } from "../neucleos/useWorldLiveness";
import type { SessionLivenessNode, SessionLivenessRollup } from "../neucleos/worldLiveness";
import { projectHiddenAttention, type HiddenAttentionEntry } from "../neucleos/sessionAttention";
import {
  projectWorldSessionLineage,
  projectWorldSessionViewSeats,
  projectWorldSessionRows,
  useWorldView,
  useWorldViewIfSeated,
  type WorldSessionRow,
} from "../neucleos/worldView";
import { WORLD_ROOMS_LOADING_COPY, isWorldRoomDisplayComplete } from "../neucleos/worldRoomDisplay";
import { waitForCreatedRootShell } from "../rootSessionBirth";
import {
  ROOT_SESSION_STATUS_LABELS,
  rootSessionStatusFillClass,
  rootSessionStatusRingClass,
} from "../rootSessionStrip";
import { useThreadShells } from "../state/entities";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";
import { buildThreadTurnInterruptInput } from "./ChatView.logic";
import { toastManager } from "./ui/toast";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";
import { sessionPanelDomId, sessionTabDomId } from "./sessionViewAria";

export type WorldSessionStripScope = "root" | "nested";

const NO_SLICE_NODES: ReadonlyArray<SessionLivenessNode> = [];

// NV2 — what counts as "there's activity": the session's own liveness says
// it is running or waiting on a person. Terminal states close silently.
const ACTIVE_SLICE_STATUSES = new Set(["working", "monitoring", "approval", "input"]);

/**
 * The joined Session + Agent birth state machine. A World-owned control creates
 * a parentless top-level Session/session; an Agent-owned control creates a child
 * from its exact displayed parent. Both routes use this one ceremony.
 */
/**
 * RB1 (§8, the principal's words): deck surfaces a room can front AT BIRTH.
 * Terminals and single pull requests need runtime identities, so they join
 * through the in-room deck once the room runs work — never from this menu.
 */
export const BIRTH_SURFACE_MENU = [
  { kind: "goals", label: "Goals", icon: Goal },
  { kind: "preview", label: "Browser", icon: Globe2 },
  { kind: "files", label: "Files", icon: Files },
  { kind: "agents", label: "Agents", icon: Bot },
  { kind: "settings", label: "Settings", icon: SettingsIcon },
] as const;
export type BirthSurfaceKind = (typeof BIRTH_SURFACE_MENU)[number]["kind"];

function frontSurfaceAtBirth(worldKey: string, sessionId: string, kind: BirthSurfaceKind): void {
  const store = useBoardDeckStore.getState();
  const scope = { worldKey, sessionId };
  if (kind === "preview") {
    store.openBrowser(scope, null);
    return;
  }
  store.open(scope, kind);
}

export function useNewSessionBirth({
  birthSource = "parent",
  goalId = null,
  onOpened,
  parentId,
  workroomKey = null,
}: {
  readonly birthSource?: "world" | "parent";
  /** Explicit exact-goal binding for a goal-spawned child; null inherits by law. */
  readonly goalId?: string | null;
  readonly onOpened?: () => void;
  readonly parentId: string | null;
  /** The named workroom scope; null means a general session. */
  readonly workroomKey?: string | null;
}) {
  const { acceptReadback, acceptEntryReadback, state } = useWorldView();
  const boardOutcome = useLaunchableWorkroomsOutcome(
    state.kind === "world-ready" ? state.worldKey : null,
  );
  const resolveBoard = (key: string) =>
    boardOutcome.loading || boardOutcome.refusal !== null
      ? undefined
      : boardOutcome.boards.find((row) => row.key === key && row.launchable);
  const shells = useThreadShells();
  const launchProjects = useWorldSessionLaunchProjects();
  const bindingsByEnvironment = useWorldWorkspaceBindings();
  const roomUniverseByCoordinate = useRoomUniverseStore((store) => store.byCoordinate);
  const createThread = useAtomCommand(threadEnvironment.create, { reportFailure: false });
  const navigate = useNavigate();
  const [modelSelection, setModelSelection] = useState<ModelSelection | null>(null);
  const [label, setLabel] = useState("");
  const [assignment, setAssignment] = useState("");
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>(goalId ? [goalId] : []);
  useEffect(() => {
    setSelectedGoalIds(goalId ? [goalId] : []);
  }, [goalId, parentId]);
  const owningRoom =
    state.kind === "world-ready" && parentId
      ? (state.index.rooms.find((room) => room.roomId === parentId) ??
        roomOfSession(state.index, parentId))
      : undefined;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partialSessionId, setPartialSessionId] = useState<string | null>(null);
  const sourceSessionId = state.kind === "world-ready" ? parentId : null;
  const sourceExecution = useMemo(() => {
    if (birthSource !== "parent" || state.kind !== "world-ready" || sourceSessionId === null) {
      return null;
    }
    const seats = projectWorldSessionViewSeats({
      activeSessionId: sourceSessionId,
      index: state.index,
      shells,
      worldKey: state.worldKey,
    });
    return seats.findLast((seat) => seat.slice.sessionId === sourceSessionId)?.execution ?? null;
  }, [birthSource, shells, sourceSessionId, state]);
  const worldGrounding = useMemo(
    () =>
      birthSource !== "world" || state.kind !== "world-ready"
        ? null
        : resolveWorldSessionLaunchGrounding({
            worldKey: state.worldKey,
            projects: launchProjects,
            bindingsByEnvironment,
            parentExecution: null,
          }),
    [bindingsByEnvironment, birthSource, launchProjects, state],
  );
  const worldProject =
    worldGrounding !== null && worldGrounding.kind === "bound" ? worldGrounding.project : null;
  const ensureFilesProject = useEnsureWorldFilesProject();
  const worldGroundingEnvironmentId =
    worldGrounding === null
      ? null
      : worldGrounding.kind === "bound"
        ? worldGrounding.project.environmentId
        : worldGrounding.kind === "files-root"
          ? worldGrounding.environmentId
          : null;
  const modelEnvironmentId =
    birthSource === "parent"
      ? (sourceExecution?.environmentId ?? null)
      : worldGroundingEnvironmentId;
  const modelWorldKey = state.kind === "world-ready" ? state.worldKey : null;
  useEffect(() => {
    setModelSelection(null);
  }, [parentId, birthSource, modelEnvironmentId, modelWorldKey]);
  const worldFilesRoot =
    state.kind !== "world-ready" || worldGroundingEnvironmentId === null
      ? null
      : resolveWorldFilesRoot({
          worldKey: state.worldKey,
          environmentId: worldGroundingEnvironmentId,
          bindingsByEnvironment,
        });
  const parentSelection = useMemo(() => {
    if (
      birthSource !== "parent" ||
      state.kind !== "world-ready" ||
      parentId === null ||
      sourceExecution === null
    ) {
      return null;
    }
    return (
      Object.values(roomUniverseByCoordinate).find(
        (
          entry,
        ): entry is Extract<RoomUniverseEntry, { state: "complete" | "paged" | "selection" }> =>
          (entry.state === "complete" || entry.state === "paged" || entry.state === "selection") &&
          entry.coordinate.worldKey === state.worldKey &&
          entry.coordinate.roomId ===
            state.index.sessions.find((session) => session.sessionId === parentId)?.roomId &&
          entry.coordinate.sessionId === parentId &&
          entry.coordinate.agentEnvironmentId === String(sourceExecution.environmentId) &&
          entry.coordinate.agentThreadId === String(sourceExecution.id),
      ) ?? null
    );
  }, [birthSource, parentId, roomUniverseByCoordinate, sourceExecution, state]);
  // Both transports retain the exact selection head. The birth server reads
  // and verifies its members at this cut, paging/staging a manifest when large.
  // UI pages are display state, never the child's inherited member list.
  const readableParentSelection = parentSelection;
  const blockedReason =
    state.kind !== "world-ready" || (birthSource === "parent" && parentId === null)
      ? "The World is not ready yet."
      : birthSource === "world"
        ? worldGrounding === null
          ? "The World is not ready yet."
          : worldGrounding.kind === "unbound"
            ? "This World has no folder to run in yet. Bind its Files folder — or choose a default project — in Connections."
            : worldGrounding.kind === "binding-broken"
              ? "The bound default project can’t be found on this machine. Choose another in Connections."
              : worldGrounding.kind === "bound" && worldFilesRoot === null
                ? "Bind the separate non-code World Files folder before starting its session."
                : worldProject?.modelSelection === null && modelSelection === null
                  ? "Configure the bound project’s provider before starting its session."
                  : null
        : sourceExecution === null
          ? "The addressed parent session is not available."
          : readableParentSelection === null
            ? "The parent boundary is not readable yet; no child will be invented from a default."
            : null;
  const canSubmit = blockedReason === null;
  const launcher = useMemo(
    () =>
      createWorldSessionExecutionLauncher({
        openSession: ({
          contextBirth,
          goalId: boundGoalId,
          goalIds: boundGoalIds,
          assignment: expectedResult,
          label: exactLabel,
          parentId: parent,
          sessionId,
          worldKey,
        }) =>
          openWorldSession({
            worldKey,
            sessionId,
            label: exactLabel,
            parentId: parent,
            ...(boundGoalId === undefined ? {} : { goalId: boundGoalId }),
            ...(boundGoalIds === undefined ? {} : { goalIds: boundGoalIds }),
            ...(expectedResult === undefined ? {} : { assignment: expectedResult }),
            ...(contextBirth === undefined ? {} : { contextBirth }),
          }),
        dispatchExecution: (environmentId, input) =>
          createThread({ environmentId, input }).then((result) => result._tag !== "Failure"),
        executionArrived: waitForCreatedRootShell,
        // JB1: the prepared identity pair becomes a durable server-side
        // intent before dispatch, so a reload can query what is unsettled.
        recordAttempt: recordWorldSessionBirthAttempt,
        initializeContext: ({ worldKey, roomId, sessionId, environmentId, threadId, boardKey }) =>
          initializeBoardContextBirth({
            worldKey,
            roomId,
            sessionId,
            agentEnvironmentId: String(environmentId),
            agentThreadId: String(threadId),
            boardKey: boardKey ?? null,
          }),
      }),
    [createThread],
  );

  const finish = async (
    outcome: WorldSessionExecutionBirthOutcome,
    sessionId: string,
    effectiveBoardKey: string | null,
  ) => {
    setSubmitting(false);
    if (outcome._tag === "Refused") {
      setError(
        outcome.reason === "source-execution-required"
          ? "Start this session first; a sub-session starts from it."
          : outcome.reason === "source-coordinate-mismatch"
            ? "This conversation doesn't belong to this session."
            : outcome.reason === "empty-label"
              ? "Name the Session before opening it."
              : outcome.reason === "board-title-reserved"
                ? "That name belongs to a Workroom. Open it from its Workroom chip in the dock instead."
                : "The World or Session address is invalid.",
      );
      return;
    }
    if (outcome._tag === "ReservationFailed") {
      setError("The new session couldn't be started, and nothing was opened. Try again.");
      return;
    }
    if (outcome._tag === "WorldFailed") {
      // A confirmed selection CAS refusal wrote no child. A new explicit
      // submit may use the reviewed selection; uncertain writes keep retrying
      // their original identity and receipt.
      setPartialSessionId(outcome.retryable ? sessionId : null);
      setError(outcome.reason);
      return;
    }
    acceptEntryReadback(outcome.entry);
    if (outcome._tag === "DispatchFailed") {
      // JB5: a failed thread-create is told apart from an unobserved one —
      // the Session exists, the session does not, and retry keeps the identity.
      setPartialSessionId(sessionId);
      setError(
        "The session is in the World, but its conversation wasn't created. Retry to finish starting it.",
      );
      return;
    }
    if (outcome._tag === "Partial") {
      setPartialSessionId(sessionId);
      setError(
        outcome.reason === "context-unconfirmed"
          ? `${outcome.detail ?? "The session's starting context wasn't confirmed."} Nothing was sent to Cleo yet. Retry to finish starting it.`
          : "The session is in the World, but its conversation can't be read yet. Retry to finish starting it.",
      );
      return;
    }
    setPartialSessionId(null);
    setLabel("");
    setError(null);
    // SB3: a board birth fronts the board's body as the new room's default —
    // the deck is claimed BEFORE navigation so ensureDeckDefaultSurface sees
    // an initialized deck and never overrides it with Goals. A plain birth
    // (null board) keeps today's behavior byte-identical.
    if (effectiveBoardKey !== null && state.kind === "world-ready") {
      const board = resolveBoard(effectiveBoardKey);
      const boardKind = board === undefined ? null : workroomBoardKind(board.key);
      if (boardKind !== null && isSingletonBoardKind(boardKind)) {
        useBoardDeckStore
          .getState()
          .openBoardSurfaceAtBirth({ worldKey: state.worldKey, sessionId }, boardKind);
      }
    }
    await navigate({
      to: "/session/$sessionId",
      params: { sessionId },
    } as never);
    // The address is the room authority. Closing the birth popover first can
    // unseat this hook while the router transition is still outstanding,
    // leaving the new tab present but the prior nested room fronted (NV6).
    // Commit the same bare Session navigation used by an ordinary tab, then
    // close its transient birth surface.
    onOpened?.();
  };

  const run = async (rawLabel: string, effectiveBoardKey: string | null) => {
    if (state.kind !== "world-ready" || submitting) return;
    // A board-bearing birth has exactly one lawful name: the board's title.
    // Resolve it here, at the shared launch seam, so no caller can pair a
    // workroomKey with an arbitrary or edited session label.
    const effectiveBoard = effectiveBoardKey === null ? null : resolveBoard(effectiveBoardKey);
    if (effectiveBoardKey !== null && effectiveBoard === undefined) {
      setError(
        boardOutcome.loading
          ? "Reading the World’s current board access…"
          : (boardOutcome.refusal?.reason ?? "This board is not available in this World."),
      );
      return;
    }
    const exactLabel = effectiveBoard?.title ?? rawLabel.trim();
    if (!exactLabel) return;
    if (effectiveBoardKey !== null) {
      setSubmitting(true);
      setError(null);
      const outcome = await activateOrCreateWorkroom({
        worldKey: state.worldKey,
        index: state.index,
        workroomKey: effectiveBoardKey,
        title: exactLabel,
        acceptReadback,
        acceptEntryReadback,
        navigate: ({ sessionId }) =>
          navigate({ to: "/session/$sessionId", params: { sessionId } } as never),
      });
      setSubmitting(false);
      if (!outcome.ok) {
        setError(outcome.reason);
        return;
      }
      onOpened?.();
      return;
    }
    setSubmitting(true);
    setError(null);
    const sessionId = `S_${randomUUID().replaceAll("-", "")}`;
    if (!canSubmit) {
      setSubmitting(false);
      setError(blockedReason);
      return;
    }
    if (birthSource === "parent" && parentId === null) {
      setSubmitting(false);
      setError(blockedReason ?? "The World is not ready yet.");
      return;
    }
    let outcome: WorldSessionExecutionBirthOutcome;
    if (birthSource === "world") {
      // A root session is born INTO the active room (RM-CAN-05): its parent is
      // the room of the addressed session, or the World's standing room.
      // A world birth into a NAMED room (a workroom's chip) passes the room id as its parent.
      const activeRoomId =
        (parentId !== null && ROOM_ID_PATTERN.test(parentId) ? parentId : undefined) ??
        (state.activeSessionId === null
          ? undefined
          : roomOfSession(state.index, state.activeSessionId)?.roomId) ??
        state.index.rootRoomId;
      outcome = await launcher.openAndLaunch({
        birthSource: "world",
        worldKey: state.worldKey,
        sessionId,
        label: exactLabel,
        parentId: activeRoomId,
        // The Cowork lane: without a bound default project the session
        // runs in the World's Files folder via its folder-project.
        project:
          worldProject ??
          (worldGrounding?.kind === "files-root"
            ? await ensureFilesProject({
                environmentId: worldGrounding.environmentId,
                filesRoot: worldGrounding.filesRoot,
                projects: launchProjects,
                worldName:
                  useNeucleosWorldStore
                    .getState()
                    .worlds.find((world) => world.worldKey === state.worldKey)?.name ??
                  state.worldKey,
              })
            : null),
        filesRoot: worldFilesRoot,
        ...(modelSelection ? { modelSelection } : {}),
        ...(effectiveBoardKey === null ? {} : { workroomKey: effectiveBoardKey }),
      });
    } else if (sourceExecution === null || readableParentSelection === null) {
      outcome = {
        _tag: "Refused",
        reason: "source-execution-required",
      };
    } else {
      const birthOutcome =
        await orderContextBirthSelection<WorldSessionExecutionBirthOutcome | null>(
          readableParentSelection,
          (contextBirth) =>
            launcher.openAndLaunch({
              birthSource: "parent",
              worldKey: state.worldKey,
              sessionId,
              label: exactLabel,
              parentId: parentId as string,
              ...(selectedGoalIds.length === 1
                ? { goalId: selectedGoalIds[0]! }
                : { goalIds: selectedGoalIds }),
              ...(assignment.trim() ? { assignment: assignment.trim() } : {}),
              source: sourceExecution,
              ...(modelSelection ? { modelSelection } : {}),
              contextBirth,
            }),
          (reason) => {
            setSubmitting(false);
            setError(reason);
            return null;
          },
        );
      if (birthOutcome === null) return;
      outcome = birthOutcome;
    }
    await finish(outcome, sessionId, effectiveBoardKey);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (partialSessionId !== null) {
      await retry();
      return;
    }
    await run(label, workroomKey);
  };

  const launchBoard = async (boardKey: string) => {
    await run("", boardKey);
  };

  const retry = async () => {
    if (partialSessionId === null) return;
    setSubmitting(true);
    const outcome = await launcher.retryPartial();
    if (outcome._tag === "Refused") {
      setSubmitting(false);
      setError("This retry is no longer available. Start the session again.");
      return;
    }
    await finish(outcome, partialSessionId, workroomKey);
  };

  return {
    modelEnvironmentId,
    modelWorldKey,
    modelSelection,
    setModelSelection,
    boundGoalId: birthSource === "parent" ? (selectedGoalIds[0] ?? null) : null,
    goalAssignment:
      state.kind === "world-ready" && owningRoom
        ? {
            worldKey: state.worldKey,
            roomGoalIds: owningRoom.goalIds ?? (owningRoom.goalId ? [owningRoom.goalId] : []),
          }
        : null,
    selectedGoalIds,
    setSelectedGoalIds,
    assignment,
    setAssignment,
    clearError: () => setError(null),
    error,
    bindWorldFiles: () => openWorldRoomConnections(),
    blockedReason,
    canBindWorldFiles:
      birthSource === "world" &&
      (worldGrounding?.kind === "bound" || worldGrounding?.kind === "unbound") &&
      worldFilesRoot === null,
    canSubmit,
    kind: birthSource,
    label,
    launchBoard,
    partialSessionId,
    retry,
    setLabel,
    sourceExecution,
    submit,
    submitting,
  };
}

export type NewSessionBirth = ReturnType<typeof useNewSessionBirth>;

/** The board a birth surface is opening — titled by the registry, never typed. */
export interface BirthBoard {
  readonly workroomKey: string;
  readonly title: string;
}

export function NewSessionBirthForm({
  birth,
  board = null,
  headline,
  launchLabel = "Start nested session from parent",
}: {
  readonly birth: NewSessionBirth;
  /**
   * DATED 2026-09-03: a board is an app — its session wears the registry
   * title and is already being born when this surface shows. In board mode
   * the surface is only progress and honest refusal: no name field, no
   * naming button. Null is the general Cleo slice, which the person names.
   */
  readonly board?: BirthBoard | null;
  readonly headline: string;
  readonly launchLabel?: string;
}): ReactElement {
  if (board !== null) {
    // The one sentence a board surface may speak: the refusal of record,
    // else the standing block. Never both, never a name prompt.
    const sentence = birth.error ?? (birth.partialSessionId === null ? birth.blockedReason : null);
    return (
      <div className="space-y-3" data-birth-board={board.workroomKey}>
        <p className="text-sm font-medium text-foreground">{board.title}</p>
        {sentence === null ? null : (
          <p
            className={`text-xs leading-4 ${birth.error === null ? "text-muted-foreground" : "text-destructive"}`}
            role={birth.error === null ? undefined : "alert"}
          >
            {sentence}
          </p>
        )}
        {birth.canBindWorldFiles &&
        birth.blockedReason !== null &&
        birth.partialSessionId === null ? (
          <Button
            className="w-full"
            onClick={birth.bindWorldFiles}
            size="sm"
            type="button"
            variant="outline"
          >
            Bind World Files
          </Button>
        ) : null}
        {birth.partialSessionId !== null ? (
          <Button
            className="w-full"
            disabled={birth.submitting}
            onClick={() => void birth.retry()}
            size="sm"
            type="button"
          >
            {birth.submitting ? "Checking…" : "Retry same Agent"}
          </Button>
        ) : !birth.submitting && sentence !== null ? (
          <Button
            className="w-full"
            disabled={!birth.canSubmit}
            onClick={() => void birth.launchBoard(board.workroomKey)}
            size="sm"
            type="button"
          >
            Open {board.title}
          </Button>
        ) : (
          <Button className="w-full" disabled size="sm" type="button">
            Opening…
          </Button>
        )}
      </div>
    );
  }
  return (
    <form className="space-y-3" onSubmit={(event) => void birth.submit(event)}>
      <div>
        <p className="text-sm font-medium text-foreground">{headline}</p>
        <p className="mt-1 text-xs leading-4 text-muted-foreground">
          It is persisted in the World and read back before this tab opens.
        </p>
      </div>
      {birth.boundGoalId === null ? null : (
        <p
          className="text-xs leading-4 text-muted-foreground"
          data-birth-bound-goal={birth.boundGoalId}
        >
          Bound to goal {birth.boundGoalId}
        </p>
      )}
      {birth.goalAssignment ? (
        <GoalAssignment
          {...birth.goalAssignment}
          value={birth.selectedGoalIds}
          onChange={birth.setSelectedGoalIds}
          disabled={birth.submitting || birth.partialSessionId !== null}
        />
      ) : null}
      {birth.modelEnvironmentId ? (
        <SessionBirthModelChoice
          key={birth.modelEnvironmentId}
          environmentId={birth.modelEnvironmentId}
          worldKey={birth.modelWorldKey}
          value={birth.modelSelection}
          onChange={birth.setModelSelection}
          disabled={birth.submitting || birth.partialSessionId !== null}
        />
      ) : null}
      <textarea
        aria-label="Expected session result"
        className="w-full rounded-md border border-input bg-background p-2 text-sm"
        placeholder="What should this session deliver?"
        maxLength={4000}
        disabled={birth.submitting || birth.partialSessionId !== null}
        value={birth.assignment}
        onChange={(event) => birth.setAssignment(event.target.value)}
      />
      <Input
        autoFocus
        aria-label="Session name"
        disabled={birth.partialSessionId !== null}
        maxLength={160}
        onChange={(event) => birth.setLabel(event.target.value)}
        placeholder="What is this room for?"
        value={birth.label}
      />
      {birth.blockedReason === null || birth.partialSessionId !== null ? null : (
        <p className="text-xs leading-4 text-muted-foreground">{birth.blockedReason}</p>
      )}
      {birth.canBindWorldFiles &&
      birth.blockedReason !== null &&
      birth.partialSessionId === null ? (
        <Button
          className="w-full"
          onClick={birth.bindWorldFiles}
          size="sm"
          type="button"
          variant="outline"
        >
          Bind World Files
        </Button>
      ) : null}
      {birth.error === null ? null : (
        <p className="text-xs leading-4 text-destructive" role="alert">
          {birth.error}
        </p>
      )}
      {birth.partialSessionId === null ? (
        <Button
          className="w-full"
          disabled={!birth.label.trim() || !birth.canSubmit || birth.submitting}
          size="sm"
          type="submit"
        >
          {birth.submitting ? "Opening…" : launchLabel}
        </Button>
      ) : (
        <Button
          className="w-full"
          disabled={birth.submitting}
          onClick={() => void birth.retry()}
          size="sm"
          type="button"
        >
          {birth.submitting ? "Checking…" : "Retry same Agent"}
        </Button>
      )}
    </form>
  );
}

export function NewSessionButton({
  origin,
  parentId,
}: {
  readonly origin: "world" | "parent";
  readonly parentId: string | null;
}): ReactElement {
  const [open, setOpen] = useState(false);
  // §8, the principal's settled ruling: "that World plus … should only be a
  // new session" — the top + always makes a fresh Cleo session with the
  // World as parent. The surface menu lives ONE LEVEL DOWN, in the room:
  // "the only nested thing that happens is a session. This would just open
  // a browser, for example." — a surface pick opens that tab in the CURRENT
  // room's deck: no birth, no ceremony, nothing persisted.
  const [surfacePick, setSurfacePick] = useState<BirthSurfaceKind | "session" | null>(null);
  const [worldBoardPick, setWorldBoardPick] = useState<string | null>(null);
  const { state: roomState } = useWorldView();
  const launchableBoardOutcome = useLaunchableWorkroomsOutcome(
    roomState.kind === "world-ready" ? roomState.worldKey : null,
  );
  const worldSessionBoards = deskSessionMenuEntries(launchableBoardOutcome.boards);
  const surfaceSession =
    roomState.kind === "world-ready"
      ? roomState.index.sessions.find((session) => session.sessionId === parentId)
      : undefined;
  const surfaceKinds = new Set(
    launchableBoardOutcome.loading ||
      launchableBoardOutcome.refusal !== null ||
      surfaceSession === undefined
      ? []
      : launchableBoardOutcome.boards
          .filter((row) => row.launchable && (isRootSession(surfaceSession) || row.nestable))
          .map((row) => row.key),
  );
  const picked =
    origin === "world" ? (worldSessionBoards.length === 0 ? "session" : surfacePick) : surfacePick;
  const worldBoardTitle =
    worldBoardPick === null
      ? null
      : (worldSessionBoards.find((entry) => entry.workroomKey === worldBoardPick)?.title ?? null);
  const birth = useNewSessionBirth({
    birthSource: origin,
    onOpened: () => setOpen(false),
    parentId,
    workroomKey: worldBoardPick,
  });
  const pickSurface = (kind: BirthSurfaceKind) => {
    if (roomState.kind !== "world-ready" || parentId === null || !surfaceKinds.has(kind)) return;
    frontSurfaceAtBirth(roomState.worldKey, parentId, kind);
    setOpen(false);
  };
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSurfacePick(null);
          setWorldBoardPick(null);
          if (birth.partialSessionId === null) birth.clearError();
        }
      }}
    >
      {/* Dated 2026-09-02 (the principal's crop, "make this have the circle
          like beside the Goals tab"): the birth door wears the DECK PLUS's
          ringed circle — one launch mark everywhere, sized by the same
          --deck-plus-size and ringed by the same [data-deck-plus] rule. */}
      <PopoverTrigger
        render={
          <button
            aria-label={
              origin === "world"
                ? "Start new general session from the World"
                : "Start nested session from this parent"
            }
            className="flex size-[var(--deck-plus-size)] shrink-0 cursor-pointer items-center justify-center self-center rounded-full text-foreground/80 hover:text-foreground"
            data-deck-plus=""
            data-world-session-new={origin}
            type="button"
          />
        }
      >
        <Plus aria-hidden className="size-3" strokeWidth={2.2} />
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-72">
        {launchableBoardOutcome.refusal === null ? null : (
          <p className="px-2 pb-2 text-xs text-muted-foreground" role="alert">
            {launchableBoardOutcome.refusal.reason}
          </p>
        )}
        {picked === null ? (
          origin === "world" ? (
            <div className="flex flex-col gap-0.5" data-world-session-birth-menu="world">
              <button
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                data-birth-menu-session=""
                onClick={() => setSurfacePick("session")}
                type="button"
              >
                <MessageSquarePlus aria-hidden className="size-4 shrink-0" />
                New general session
              </button>
              <div aria-hidden className="mx-2 my-0.5 border-t border-border/60" />
              {worldSessionBoards.map((entry) => (
                <button
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  data-birth-menu-board={entry.workroomKey}
                  key={entry.workroomKey}
                  onClick={() => {
                    setWorldBoardPick(entry.workroomKey);
                    setSurfacePick("session");
                    void birth.launchBoard(entry.workroomKey);
                  }}
                  type="button"
                >
                  <LayoutDashboard aria-hidden className="size-4 shrink-0" />
                  Open {entry.title} session
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5" data-world-session-birth-menu="">
              <button
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                data-birth-menu-session=""
                onClick={() => setSurfacePick("session")}
                type="button"
              >
                <MessageSquarePlus aria-hidden className="size-4 shrink-0" />
                New nested session
              </button>
              <div aria-hidden className="mx-2 my-0.5 border-t border-border/60" />
              {BIRTH_SURFACE_MENU.filter((entry) => surfaceKinds.has(entry.kind)).map((entry) => (
                <button
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  data-birth-surface={entry.kind}
                  key={entry.kind}
                  onClick={() => pickSurface(entry.kind)}
                  type="button"
                >
                  <entry.icon aria-hidden className="size-4 shrink-0" />
                  {entry.label}
                </button>
              ))}
            </div>
          )
        ) : (
          <NewSessionBirthForm
            birth={birth}
            board={
              worldBoardPick === null || worldBoardTitle === null
                ? null
                : { workroomKey: worldBoardPick, title: worldBoardTitle }
            }
            headline={
              origin === "world" ? "New general session from the World" : "New nested session"
            }
            launchLabel={
              origin === "world"
                ? "Start new general session from World"
                : "Start nested session from parent"
            }
          />
        )}
      </PopoverPopup>
    </Popover>
  );
}

/** NV4 — how many attention chips render before the rest fold to a +N count. */
export const ATTENTION_CHIP_CAP = 3;

/**
 * NV4 — the breadcrumb's attention zone: the hidden needs-a-person Sessions,
 * lifted as chips onto the row that already says where the person is. Quiet
 * by law — renders NOTHING while the set is empty, so the row only lights
 * when someone is actually needed. A chip navigates to its Session's route
 * exactly as a tab does (never a second navigation species), and past the
 * cap the rest fold into a +N count.
 */
function SessionAttentionZone({
  entries,
}: {
  readonly entries: ReadonlyArray<HiddenAttentionEntry>;
}): ReactElement | null {
  const navigate = useNavigate();
  if (entries.length === 0) return null;
  const shown = entries.slice(0, ATTENTION_CHIP_CAP);
  const overflow = entries.length - shown.length;
  return (
    <div
      aria-label="Needs a person"
      className="ml-auto flex shrink-0 items-center gap-1 pl-3"
      data-slice-attention-zone=""
      role="group"
    >
      {shown.map((entry) => (
        <button
          aria-label={`${entry.label}, ${ROOT_SESSION_STATUS_LABELS[entry.status]}, hidden behind the current view`}
          className="flex h-5 max-w-40 shrink-0 items-center gap-1.5 rounded-full border border-border/60 px-2 text-[10px] text-muted-foreground transition-colors duration-150 hover:text-foreground forced-colors:border-[ButtonText]"
          data-slice-attention-chip={entry.sessionId}
          data-slice-attention-status={entry.status}
          key={entry.sessionId}
          onClick={() => {
            void navigate({
              to: "/session/$sessionId",
              params: { sessionId: entry.sessionId },
            } as never);
          }}
          title={`${entry.label} · ${ROOT_SESSION_STATUS_LABELS[entry.status]} — hidden behind the current view`}
          type="button"
        >
          <span
            aria-hidden="true"
            className={`size-1.5 shrink-0 rounded-full forced-colors:bg-[ButtonText] ${rootSessionStatusFillClass(entry.status)}`}
          />
          <span className="truncate">{entry.label}</span>
        </button>
      ))}
      {overflow > 0 ? (
        <span
          className="text-[10px] text-muted-foreground"
          data-slice-attention-overflow={String(overflow)}
          title={`${overflow} more hidden ${overflow === 1 ? "session needs" : "sessions need"} a person`}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The rail-law chip's seat (FD-CAN-10, superseded dated 2026-09-01 by
 * FD-CAN-18): the chip is a ROOM instrument and rides the DECK band row's
 * right cluster now — the workbench row over the rails it governs. Only
 * World rooms publish the law; it renders nothing while no room publishes.
 */
function RootStripRailLawSeat(): ReactElement | null {
  const view = useRailLawStore((state) => state.view);
  const setCap = useRailLawStore((state) => state.setCap);
  if (view === null || setCap === null) return null;
  // Nothing nested, nothing to seat — not even the seat's own padding
  // (principal ruling 2026-09-02): the chip appears only once a sub-session
  // primitive exists.
  if (view.total <= 1) return null;
  return (
    <div className="flex shrink-0 items-center pl-3" data-rail-law-seat="">
      <RailLawControl onCap={setCap} railCap={view.railCap} total={view.total} />
    </div>
  );
}

/**
 * FD-CAN-18 (the principal's words, 2026-09-01: "do you see any issue
 * moving the rail nesting down (then we can remove that entire row,
 * matching the design we did"): the root utilities row — the strip's LAST
 * row — is retired. The attention zone (every NV4 zone law intact) and the
 * rail-law chip reseat as this self-sufficient cluster at the DECK band
 * row's right end, beside the split controls. Peeks the room: renders
 * nothing unseated, so the deck bar stays safe wherever it mounts.
 */
export function DeckUtilitiesCluster(): ReactElement | null {
  const room = useWorldViewIfSeated();
  if (room === null || room.state.kind !== "world-ready") return null;
  return <DeckUtilitiesClusterSeated room={room} />;
}

function DeckUtilitiesClusterSeated({
  room,
}: {
  readonly room: NonNullable<ReturnType<typeof useWorldViewIfSeated>>;
}): ReactElement | null {
  const { state } = room;
  const liveness = useSessionLivenessRollups(
    state.kind === "world-ready" ? state.worldKey : null,
    state.kind === "world-ready" ? state.index.sessions : NO_SLICE_NODES,
  );
  if (state.kind !== "world-ready") return null;
  return (
    <div className="flex flex-none items-center gap-1" data-deck-utilities="">
      {isWorldRoomDisplayComplete(state.index) ? null : (
        // Hidden needs live in rooms not read yet: say so rather than stay quiet.
        <span
          className="shrink-0 pl-3 text-[10px] text-muted-foreground"
          data-world-rooms-loading=""
          role="status"
        >
          {WORLD_ROOMS_LOADING_COPY}
        </span>
      )}
      <SessionAttentionZone
        entries={projectHiddenAttention({
          index: state.index,
          activeSessionId: state.activeSessionId,
          rollups: liveness,
        })}
      />
      <RootStripRailLawSeat />
    </div>
  );
}

/**
 * NV2 — the close/reopen ceremony as ONE law, wherever session tabs seat
 * (the strip's rows; the deck's merged row after §8's one-bar ruling). Close
 * is never stop: going headless flips the World's own engagement word
 * through the one door verb; the session keeps running and ancestors keep
 * its ghost dot. Closing an addressed Session leaves the route first so the
 * URL is never stranded, and a live session warns with exactly
 * Stop / Wait / Go headless before anything closes.
 */
export function useSessionCloseCeremony({
  activeSessionId,
  index,
  liveness,
  worldKey,
}: {
  readonly activeSessionId: string | null;
  readonly index: WorldRoomDirectory | null;
  readonly liveness: ReadonlyMap<string, SessionLivenessRollup>;
  readonly worldKey: string | null;
}): {
  /** The pending-activity warning dialog; renders nothing while idle. */
  readonly closeWarning: ReactElement;
  readonly reopenSession: (sessionId: string) => void;
  readonly requestClose: (sessionId: string, label: string) => void;
} {
  const { acceptEntryReadback } = useWorldView();
  const shells = useThreadShells();
  const navigate = useNavigate();
  const interruptTurn = useAtomCommand(threadEnvironment.interruptTurn, { reportFailure: false });
  // Which Session asked to close while its session was live. Null = no dialog.
  const [pendingClose, setPendingClose] = useState<{
    readonly sessionId: string;
    readonly label: string;
  } | null>(null);
  const goHeadless = async (sessionId: string) => {
    if (worldKey === null || index === null) return;
    const addressedLineage =
      activeSessionId === null
        ? new Set<string>()
        : new Set(
            projectWorldSessionLineage(index, activeSessionId).map((slice) => slice.sessionId),
          );
    if (addressedLineage.has(sessionId)) {
      const parent =
        index.sessions.find((slice) => slice.sessionId === sessionId)?.parentId ?? null;
      // A World-rooted Session (parent = the World itself) lands on the
      // authoritative root Session, never on "/": the landing path triggers
      // WS8 restoration, whose remembered room is the Session being closed —
      // restoring it would re-address the tab the instant it went headless.
      // A root session (parent = its room) lands on the room's entry session, never on "/".
      const parentSession =
        parent !== null && index.sessions.some((row) => row.sessionId === parent) ? parent : null;
      const entry = entrySessionOf(index)?.sessionId ?? null;
      const landing = parentSession ?? (entry === sessionId ? null : entry);
      if (landing !== null) {
        await navigate({ to: "/session/$sessionId", params: { sessionId: landing } } as never);
      } else {
        await navigate({ to: "/" } as never);
      }
    }
    const outcome = await setWorldSessionEngagement({
      worldKey,
      sessionId,
      engagement: "headless",
    });
    if (!outcome.ok) {
      toastManager.add({ type: "error", title: "Close refused", description: outcome.reason });
      return;
    }
    acceptEntryReadback(outcome.entry);
  };
  const reopenSession = async (sessionId: string) => {
    if (worldKey === null) return;
    const outcome = await setWorldSessionEngagement({ worldKey, sessionId, engagement: "engaged" });
    if (!outcome.ok) {
      toastManager.add({ type: "error", title: "Reopen refused", description: outcome.reason });
      return;
    }
    acceptEntryReadback(outcome.entry);
  };
  const requestClose = (sessionId: string, label: string) => {
    const own = liveness.get(sessionId)?.own ?? null;
    if (own !== null && ACTIVE_SLICE_STATUSES.has(own)) {
      setPendingClose({ sessionId, label });
      return;
    }
    void goHeadless(sessionId);
  };
  const stopAndClose = async () => {
    if (pendingClose === null || worldKey === null || index === null) return;
    const { sessionId } = pendingClose;
    setPendingClose(null);
    const execution =
      projectWorldSessionViewSeats({
        activeSessionId: sessionId,
        index,
        shells,
        worldKey,
      }).findLast((seat) => seat.slice.sessionId === sessionId)?.execution ?? null;
    if (execution !== null) {
      await interruptTurn({
        environmentId: execution.environmentId,
        input: buildThreadTurnInterruptInput(execution),
      });
    }
    await goHeadless(sessionId);
  };
  const closeWarning = (
    <Dialog
      onOpenChange={(open) => {
        if (!open) setPendingClose(null);
      }}
      open={pendingClose !== null}
    >
      <DialogPopup className="max-w-md" data-world-session-close-warning="">
        <DialogHeader>
          <DialogTitle>There&apos;s activity in &ldquo;{pendingClose?.label}&rdquo;</DialogTitle>
          <DialogDescription>
            This session is still running. Stop it, wait for it to finish, or go headless — the tab
            closes while the work keeps running (its ghost dot stays on the ancestors).
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            data-close-choice="stop"
            onClick={() => void stopAndClose()}
            size="sm"
            variant="destructive-outline"
          >
            Stop
          </Button>
          <Button
            data-close-choice="wait"
            onClick={() => setPendingClose(null)}
            size="sm"
            variant="outline"
          >
            Wait
          </Button>
          <Button
            data-close-choice="headless"
            onClick={() => {
              const pending = pendingClose;
              setPendingClose(null);
              if (pending !== null) void goHeadless(pending.sessionId);
            }}
            size="sm"
          >
            Go headless
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
  return { closeWarning, reopenSession, requestClose };
}

/**
 * NV2 — the reachable closed list: headless Sessions leave their tab row but
 * never the World. One articulation for every seat session tabs occupy.
 */
export function SessionClosedList({
  onReopen,
  sessions,
}: {
  readonly onReopen: (sessionId: string) => void;
  readonly sessions: ReadonlyArray<{ readonly sessionId: string; readonly label: string }>;
}): ReactElement | null {
  if (sessions.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            // self-center: seated in the deck's items-stretch track a fixed
            // height would otherwise pin this to the row's top (dated 2026-09-02).
            className="flex h-6 shrink-0 items-center self-center rounded-md border border-transparent px-1.5 text-[10px] text-muted-foreground transition-colors duration-150 hover:text-foreground"
            data-world-session-headless-list={String(sessions.length)}
            type="button"
          />
        }
      >
        {sessions.length} closed
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-64">
        <p className="text-sm font-medium text-foreground">Closed Sessions</p>
        <p className="mt-1 text-xs leading-4 text-muted-foreground">
          Headless — still in the World; running work keeps its ghost dot on the ancestors. Reopen
          returns the tab.
        </p>
        <ul className="mt-2 space-y-1.5">
          {sessions.map((slice) => (
            <li className="flex min-w-0 items-center gap-2" key={slice.sessionId}>
              <span className="min-w-0 flex-1 truncate text-xs text-foreground">{slice.label}</span>
              <Button
                data-world-session-reopen={slice.sessionId}
                onClick={() => onReopen(slice.sessionId)}
                size="xs"
                variant="outline"
              >
                Reopen
              </Button>
            </li>
          ))}
        </ul>
      </PopoverPopup>
    </Popover>
  );
}

export function WorldSessionStrip({
  parentId,
  scope,
}: {
  readonly parentId?: string;
  readonly scope: WorldSessionStripScope;
}): ReactElement | null {
  const { state } = useWorldView();
  // The rollup hook runs unconditionally (hooks law); outside world-ready it
  // folds nothing and the map stays empty.
  const liveness = useSessionLivenessRollups(
    state.kind === "world-ready" ? state.worldKey : null,
    state.kind === "world-ready" ? state.index.sessions : NO_SLICE_NODES,
  );
  const { closeWarning, reopenSession, requestClose } = useSessionCloseCeremony({
    activeSessionId: state.kind === "world-ready" ? state.activeSessionId : null,
    index: state.kind === "world-ready" ? state.index : null,
    liveness,
    worldKey: state.kind === "world-ready" ? state.worldKey : null,
  });
  const rows =
    state.kind === "world-ready" ? projectWorldSessionRows(state.index, state.activeSessionId) : [];
  // Every strip is seated inside a world-ready room; a loading or refused
  // room is named by WorldViewStateFace in the body, never by a stub tab row.
  if (state.kind !== "world-ready") return null;
  // FD-CAN-15 (principal's annotated mock, 2026-09-01): the ROOT row's
  // horizontal tabs retired to the left rail's Sessions section — every
  // law rode along (label, dots, ghost, close ceremony, closed list, the
  // SB6 +). The root scope keeps only the utilities cluster row until
  // FD-CAN-16 unifies the band.
  const visible =
    scope === "root"
      ? []
      : parentId === undefined
        ? rows.slice(1)
        : rows.filter((row) => row.parentId === parentId);
  // A level without descendants renders nothing: no empty tab bar, no
  // creator-only chrome.
  if (scope === "nested" && visible.length === 0) return null;
  const addressedLineage = new Set(
    projectWorldSessionLineage(state.index, state.activeSessionId).map((slice) => slice.sessionId),
  );
  const labelById = new Map(state.index.sessions.map((slice) => [slice.sessionId, slice.label]));
  // FD-CAN-18 (the principal's word, 2026-09-01: "moving the rail nesting
  // down (then we can remove that entire row, matching the design we
  // did"): the LAST root row is retired. The attention zone and the
  // rail-law chip stand as DeckUtilitiesCluster at the deck band row's
  // right end now — FD-CAN-10's root-tab-row seat superseded dated. The
  // root scope renders nothing here but the close ceremony's warning.
  return (
    <div data-world-session-strip={scope}>
      {visible.map((row) => (
        <SessionTabRow
          addressedLineage={addressedLineage}
          key={row.parentId ?? "root"}
          level={rows.indexOf(row)}
          liveness={liveness}
          newSessionOrigin={scope === "root" ? "world" : "parent"}
          newSessionParentId={scope === "root" ? null : (row.parentId ?? state.activeSessionId)}
          // The tab↔panel linkage exists only where a room primitive mounts
          // the panels: a nested row with an explicit parent. The root row
          // and the shell's roomless nested strip stamp no aria-controls —
          // a dangling reference is worse than none (sessionViewAria carries
          // the one shared derivation and the root-row rationale).
          panelId={
            scope === "nested" && row.parentId !== null && parentId !== undefined
              ? sessionPanelDomId(row.parentId)
              : null
          }
          parentLabel={row.parentId === null ? null : (labelById.get(row.parentId) ?? null)}
          onReopen={reopenSession}
          onRequestClose={requestClose}
          row={row}
        />
      ))}
      {closeWarning}
    </div>
  );
}

function SessionTabRow({
  addressedLineage,
  level,
  liveness,
  newSessionParentId,
  newSessionOrigin,
  onReopen,
  onRequestClose,
  panelId,
  parentLabel,
  row,
}: {
  readonly addressedLineage: ReadonlySet<string>;
  readonly level: number;
  readonly liveness: ReadonlyMap<string, SessionLivenessRollup>;
  readonly newSessionParentId: string | null;
  readonly newSessionOrigin: "world" | "parent";
  /** FD-CAN-10: the root row's fixed right cluster (attention + rail law). */
  /** NV2: re-engage a headless Session through the one door verb. */
  readonly onReopen: (sessionId: string) => void;
  /** NV2: the close ceremony — warn on activity, else straight to headless. */
  readonly onRequestClose: (sessionId: string, label: string) => void;
  /** The room panel this row's tabs control; null = no panel is mounted. */
  readonly panelId: string | null;
  readonly parentLabel: string | null;
  readonly row: WorldSessionRow;
}): ReactElement {
  const navigate = useNavigate();
  const { listRef, onKeyDown } = useRovingTablist();
  const { ref: scrollRef, edges, onScroll } = useScrollFade<HTMLDivElement>("x");
  // NV2 — headless Sessions leave the tab row but never the World: they move
  // to the row's reachable closed list. An addressed Session stays visible
  // even mid-flip so the person is never standing in an invisible room.
  const engagedSessions = row.sessions.filter(
    (slice) => slice.engagement !== "headless" || addressedLineage.has(slice.sessionId),
  );
  const headlessSessions = row.sessions.filter(
    (slice) => slice.engagement === "headless" && !addressedLineage.has(slice.sessionId),
  );
  const selectedIndex = Math.max(
    0,
    engagedSessions.findIndex((slice) => addressedLineage.has(slice.sessionId)),
  );
  // NV1 — the approved two-tier grammar: the root row wears navbar-1/7's
  // underline tabs, nested rows wear the sub-row pill. One sliding mark per
  // row, moved by measurement. TH5's World-identity exception paints that
  // mark from World terra directly; the total canonical accent is its fallback.
  const variant = level === 0 ? "underline" : "pill";
  const activeSessionId =
    engagedSessions.find((slice) => addressedLineage.has(slice.sessionId))?.sessionId ?? null;
  const indicatorRef = useSlidingTabIndicator(
    scrollRef,
    activeSessionId,
    '[role="tab"][aria-selected="true"]',
  );
  return (
    <div
      className="ncl-reactbits flex h-10 shrink-0 items-stretch border-b border-border/60 bg-background"
      data-world-session-level={level}
    >
      <div className="relative flex min-w-0 flex-1 items-stretch">
        <div
          className="relative flex min-w-0 flex-1 items-center gap-1 overflow-x-auto px-2"
          onScroll={onScroll}
          ref={scrollRef}
        >
          {/* The one sliding mark lives OUTSIDE the tab composite so the
              tablist holds only tabs (A3-CLOSE); the scroll container is its
              offset parent, so it rides the scrolled content.
              DATED REVISION 2026-09-01 (FD-CAN-06): the ROOT row's active
              mark is the merge geometry on the selected tab — the sliding
              underline leaves that row, the principal-approved Founders
              design superseding the donor treatment there. Nested rows keep
              exactly this sliding FILLED PILL (NV1's sub-row grammar). */}
          {variant === "pill" ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-1 z-0 rounded-md opacity-0 transition-[left,width] duration-300 ease-out motion-reduce:transition-none forced-colors:hidden"
              data-indicator-for={activeSessionId ?? undefined}
              data-slice-tab-indicator={variant}
              ref={indicatorRef}
              style={{
                background: "var(--ncl-world-accent, var(--ncl-accent, var(--primary)))",
              }}
            />
          ) : null}
          {/* The tab composite holds ONLY role="tab" members: the New Session
              control stays an adjacent named control so roving Arrow/Home/End
              focus never lands on a non-tab (A3-CLOSE). */}
          <div
            aria-label={parentLabel === null ? "Cleo Sessions" : `Sessions of ${parentLabel}`}
            className="flex items-center gap-1"
            onKeyDown={onKeyDown}
            ref={listRef}
            role="tablist"
          >
            {engagedSessions.map((slice, index) => {
              const selected = addressedLineage.has(slice.sessionId);
              const rollup = liveness.get(slice.sessionId);
              const own = rollup?.own ?? null;
              const ghost = rollup?.descendants ?? null;
              // The session strip's announcement grammar, verbatim: the
              // Session's own answer first, then the subtree ghost.
              const ownAnnouncement = own === null ? "" : `, ${ROOT_SESSION_STATUS_LABELS[own]}`;
              const ghostAnnouncement =
                ghost === null ? "" : `, descendants: ${ROOT_SESSION_STATUS_LABELS[ghost]}`;
              return (
                <button
                  aria-controls={panelId ?? undefined}
                  aria-label={`${slice.label}${ownAnnouncement}${ghostAnnouncement}`}
                  aria-selected={selected}
                  id={sessionTabDomId(slice.sessionId)}
                  className={cn(
                    "group relative z-[1] flex h-7 max-w-56 shrink-0 items-center truncate rounded-md border border-transparent px-2.5 text-xs transition-colors duration-150",
                    selected
                      ? variant === "underline"
                        ? "font-medium text-foreground forced-colors:border-[Highlight] forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]"
                        : "font-medium text-[var(--rb-accent-fg,var(--ncl-accent-foreground))] forced-colors:border-[Highlight] forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]"
                      : "text-muted-foreground hover:text-foreground forced-colors:border-[ButtonText] forced-colors:text-[ButtonText]",
                  )}
                  data-world-session-descendant-status={ghost ?? undefined}
                  data-world-session-id={slice.sessionId}
                  data-world-session-status={own ?? undefined}
                  key={slice.sessionId}
                  onClick={() => {
                    if (selected) return;
                    void navigate({
                      to: "/session/$sessionId",
                      params: { sessionId: slice.sessionId },
                    } as never);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Delete" && event.key !== "Backspace") return;
                    event.preventDefault();
                    onRequestClose(slice.sessionId, slice.label);
                  }}
                  role="tab"
                  tabIndex={index === selectedIndex ? 0 : -1}
                  title={`${slice.label} · ${slice.goal?.title ?? "No goal selected"}`}
                  type="button"
                >
                  {own !== null ? (
                    <span
                      aria-hidden="true"
                      className={`mr-1.5 size-1.5 shrink-0 rounded-full forced-colors:bg-[ButtonText] ${rootSessionStatusFillClass(own)}`}
                    />
                  ) : null}
                  <span className="truncate">{slice.label}</span>
                  {ghost !== null ? (
                    // The ghost dot: hollow, colored by the subtree's most
                    // urgent status, so nested work below a collapsed level
                    // is noticeable without a second seat for it.
                    <span
                      aria-hidden="true"
                      className={`ml-1.5 size-1.5 shrink-0 rounded-full border forced-colors:border-[ButtonText] ${rootSessionStatusRingClass(ghost)}`}
                    />
                  ) : null}
                  {/* NV2 — the close X: clickable, but never a second
                      tab-stop; the keyboard path is Delete on the focused
                      tab, so roving focus stays a pure tab composite. */}
                  <span
                    aria-hidden="true"
                    className="ml-1 hidden size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100 group-hover:inline-flex"
                    data-world-session-close={slice.sessionId}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRequestClose(slice.sessionId, slice.label);
                    }}
                  >
                    <X className="size-3" />
                  </span>
                  {/* FD-CAN-06: the root row's one active mark — the
                      selected tab fuses into the frame below. */}
                  {selected && variant === "underline" ? (
                    <MergeTabMark dataCleo="root-merge-path" />
                  ) : null}
                </button>
              );
            })}
          </div>
          <NewSessionButton origin={newSessionOrigin} parentId={newSessionParentId} />
          <SessionClosedList onReopen={onReopen} sessions={headlessSessions} />
        </div>
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-y-0 left-0 w-6 bg-linear-to-r from-background to-transparent transition-opacity duration-150",
            edges.start ? "opacity-100" : "opacity-0",
          )}
          data-scroll-fade="start"
        />
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-y-0 right-0 w-6 bg-linear-to-l from-background to-transparent transition-opacity duration-150",
            edges.end ? "opacity-100" : "opacity-0",
          )}
          data-scroll-fade="end"
        />
      </div>
      {/* FD-CAN-18: the row-level utilities seat retired with the root row —
          the instruments live in DeckUtilitiesCluster on the deck band. */}
    </div>
  );
}
